// src/pages/Admin/Dashboard/components/ReglementsCrud.jsx

import React, { useMemo, useState } from "react";

import {
  FaBuilding,
  FaCreditCard,
  FaEdit,
  FaPlus,
  FaSearch,
  FaTrash,
} from "react-icons/fa";

import { toast } from "react-toastify";

import { DashboardCard, StatusBadge } from "../ui/DashboardUI";

import {
  cf,
  formatDate,
  formatDateShort,
  getApiErrorMessage,
  getPaymentStatusTone,
  getReglementStatusTone,
  getReglementUser,
  getSubscriptionStatus,
  getUserFullName,
  isPaidReglement,
  normalizeText,
} from "../utils/dashboard.utils";

import ReglementFormModal from "./ReglementFormModal";
import DeleteReglementModal from "./DeleteReglementModal";

function getClubForReglement(reglement, clubsByUser) {
  const userId = String(reglement?.user_achat_id || "");

  return Object.prototype.hasOwnProperty.call(clubsByUser, userId)
    ? clubsByUser[userId]
    : undefined;
}

export default function ReglementsCrud({
  reglements = [],
  users = [],
  clubsByUser = {},
  loading = false,
  clubsLoading = false,
  usersLoading = false,
  saving = false,
  onLoadUserClub,
  onCreate,
  onUpdate,
  onDelete,
}) {
  const [search, setSearch] = useState("");
  const [formulaFilter, setFormulaFilter] = useState("all");
  const [clubFilter, setClubFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");

  const [formModalOpen, setFormModalOpen] = useState(false);

  const [editingReglement, setEditingReglement] = useState(null);

  const [deletingReglement, setDeletingReglement] = useState(null);

  const formulas = useMemo(() => {
    return Array.from(
      new Set(reglements.map((reglement) => reglement.formule).filter(Boolean)),
    ).sort((first, second) => first.localeCompare(second, "fr"));
  }, [reglements]);

  const clubs = useMemo(() => {
    const clubsMap = new Map();

    reglements.forEach((reglement) => {
      const club = getClubForReglement(reglement, clubsByUser);

      if (club?.id && club?.nom_club) {
        clubsMap.set(String(club.id), club);
      }
    });

    return Array.from(clubsMap.values()).sort((first, second) =>
      String(first.nom_club).localeCompare(String(second.nom_club), "fr"),
    );
  }, [clubsByUser, reglements]);

  const statuses = useMemo(() => {
    return Array.from(
      new Set(
        reglements
          .map((reglement) => reglement.statut_reglement)
          .filter(Boolean),
      ),
    ).sort((first, second) => first.localeCompare(second, "fr"));
  }, [reglements]);

  const paymentMethods = useMemo(() => {
    return Array.from(
      new Set(
        reglements
          .map(
            (reglement) =>
              reglement.mode_reglement ||
              (reglement.payment_status ? "Stripe" : "Autre"),
          )
          .filter(Boolean),
      ),
    ).sort((first, second) => first.localeCompare(second, "fr"));
  }, [reglements]);

  const filteredReglements = useMemo(() => {
    const needle = normalizeText(search);

    return reglements.filter((reglement) => {
      const user = getReglementUser(reglement);

      const club = getClubForReglement(reglement, clubsByUser);

      const paymentMethod =
        reglement.mode_reglement ||
        (reglement.payment_status ? "Stripe" : "Autre");

      if (formulaFilter !== "all" && reglement.formule !== formulaFilter) {
        return false;
      }

      if (clubFilter !== "all" && String(club?.id || "") !== clubFilter) {
        return false;
      }

      if (
        statusFilter !== "all" &&
        reglement.statut_reglement !== statusFilter
      ) {
        return false;
      }

      if (
        paymentMethodFilter !== "all" &&
        paymentMethod !== paymentMethodFilter
      ) {
        return false;
      }

      if (!needle) return true;

      const searchableText = normalizeText(
        [
          user.prenom,
          user.nom,
          user.email,

          club?.nom_club,
          club?.departement_club,
          club?.adresse_club,

          reglement.formule,
          reglement.statut_reglement,
          paymentMethod,
          reglement.payment_status,
          reglement.note,
          reglement.id,
        ].join(" "),
      );

      return searchableText.includes(needle);
    });
  }, [
    clubFilter,
    clubsByUser,
    formulaFilter,
    paymentMethodFilter,
    reglements,
    search,
    statusFilter,
  ]);

  const displayedTotal = useMemo(() => {
    return filteredReglements.reduce(
      (total, reglement) => total + Number(reglement.prix_total || 0),
      0,
    );
  }, [filteredReglements]);

  const openCreateModal = () => {
    setEditingReglement(null);
    setFormModalOpen(true);
  };

  const openEditModal = (reglement) => {
    setEditingReglement(reglement);
    setFormModalOpen(true);
  };

  const closeFormModal = () => {
    if (saving) return;

    setFormModalOpen(false);
    setEditingReglement(null);
  };

  const latestPaidReglementByUser = useMemo(() => {
    const map = {};

    reglements.forEach((reglement) => {
      if (!isPaidReglement(reglement)) return;

      const userId = String(reglement.user_achat_id || "");
      if (!userId) return;

      const current = map[userId];
      const currentDate = current ? new Date(current.date_achat).getTime() : 0;
      const nextDate = new Date(reglement.date_achat).getTime();

      if (!current || nextDate > currentDate) {
        map[userId] = reglement;
      }
    });

    return map;
  }, [reglements]);

  const handleSubmit = async (payload) => {
    try {
      if (editingReglement?.id) {
        await onUpdate(editingReglement.id, payload);
        toast.success("L’achat a été modifié.");
      } else {
        await onCreate(payload);
        toast.success("Le nouvel achat a été créé.");
      }

      setFormModalOpen(false);
      setEditingReglement(null);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Impossible d’enregistrer l’achat."),
      );
    }
  };

  const handleDelete = async () => {
    if (!deletingReglement?.id) return;

    try {
      await onDelete(deletingReglement.id);

      toast.success("L’achat a été supprimé.");
      setDeletingReglement(null);
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "Impossible de supprimer l’achat."),
      );
    }
  };

  return (
    <>
      <div className="mt-5">
        <div className="mb-4 flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900 dark:text-white">
              Gestion des achats
            </h3>

            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
              {loading
                ? "Chargement des achats..."
                : `${filteredReglements.length} achat(s) affiché(s) sur ${reglements.length} • ${cf.format(displayedTotal)}`}
            </p>
          </div>

          <button
            type="button"
            onClick={openCreateModal}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 font-semibold text-white shadow-sm transition hover:bg-blue-500"
          >
            <FaPlus />
            Nouvel achat
          </button>
        </div>

        <DashboardCard className="mb-4 p-3 sm:p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-5">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Nom, email, club..."
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-3 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <select
              value={formulaFilter}
              onChange={(event) => setFormulaFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="all">Toutes les formules</option>

              {formulas.map((formula) => (
                <option key={formula} value={formula}>
                  {formula}
                </option>
              ))}
            </select>

            <select
              value={clubFilter}
              onChange={(event) => setClubFilter(event.target.value)}
              disabled={clubsLoading}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="all">
                {clubsLoading ? "Chargement des clubs..." : "Tous les clubs"}
              </option>

              {clubs.map((club) => (
                <option key={club.id} value={String(club.id)}>
                  {club.nom_club}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="all">Tous les statuts</option>

              {statuses.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>

            <select
              value={paymentMethodFilter}
              onChange={(event) => setPaymentMethodFilter(event.target.value)}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-slate-900 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="all">Tous les règlements</option>

              {paymentMethods.map((paymentMethod) => (
                <option key={paymentMethod} value={paymentMethod}>
                  {paymentMethod}
                </option>
              ))}
            </select>
          </div>
        </DashboardCard>

        <DashboardCard className="hidden overflow-hidden lg:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
              <thead className="bg-slate-50 dark:bg-slate-900/70">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Échéance
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Client
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Club
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Formule
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Règlement
                  </th>

                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Montant
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Statut
                  </th>

                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Stripe
                  </th>

                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <tr key={index}>
                      <td colSpan={9} className="px-4 py-4">
                        <div className="h-6 w-full animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
                      </td>
                    </tr>
                  ))
                ) : filteredReglements.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center">
                      <FaCreditCard className="mx-auto mb-3 text-3xl text-slate-300 dark:text-slate-700" />

                      <div className="font-semibold text-slate-700 dark:text-slate-300">
                        Aucun achat ne correspond aux filtres.
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredReglements.map((reglement) => {
                    const user = getReglementUser(reglement);

                    const club = getClubForReglement(reglement, clubsByUser);

                    const paymentMethod =
                      reglement.mode_reglement ||
                      (reglement.payment_status ? "Stripe" : "Autre");

                    const isReferenceReglement =
                      latestPaidReglementByUser[String(reglement.user_achat_id)]
                        ?.id === reglement.id;

                    const subscription = getSubscriptionStatus(
                      reglement,
                      isReferenceReglement && isPaidReglement(reglement),
                    );

                    return (
                      <tr
                        key={reglement.id}
                        className="transition hover:bg-slate-50 dark:hover:bg-slate-900/40"
                      >
                        <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {formatDate(reglement.date_achat)}
                        </td>

                        <td className="px-4 py-3 min-w-[220px]">
                          {!isPaidReglement(reglement) ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Non applicable
                            </span>
                          ) : !subscription.isReference ? (
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              Historique
                            </span>
                          ) : (
                            <div className="space-y-2">
                              <div className="flex items-center justify-between gap-2">
                                <StatusBadge tone={subscription.tone}>
                                  {subscription.label}
                                </StatusBadge>

                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {formatDateShort(subscription.expiryDate)}
                                </span>
                              </div>

                              <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
                                <div
                                  className={[
                                    "h-full rounded-full transition-all",
                                    subscription.tone === "emerald" &&
                                      "bg-emerald-500",
                                    subscription.tone === "amber" &&
                                      "bg-amber-500",
                                    subscription.tone === "rose" &&
                                      "bg-rose-500",
                                    subscription.tone === "slate" &&
                                      "bg-slate-500",
                                  ]
                                    .filter(Boolean)
                                    .join(" ")}
                                  style={{ width: `${subscription.progress}%` }}
                                />
                              </div>

                              <div className="text-xs text-slate-500 dark:text-slate-400">
                                {subscription.isExpired
                                  ? `Expiré depuis ${Math.abs(subscription.daysLeft)} jour(s)`
                                  : `${subscription.daysLeft} jour(s) restant(s)`}
                              </div>
                            </div>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {getUserFullName(user)}
                          </div>

                          <div className="text-xs text-slate-500 dark:text-slate-400">
                            {user.email || "—"}
                          </div>
                        </td>

                        <td className="px-4 py-3">
                          {club === undefined ? (
                            <div className="h-10 w-36 animate-pulse rounded-lg bg-slate-200/70 dark:bg-slate-800" />
                          ) : club ? (
                            <div>
                              <div className="font-semibold text-slate-900 dark:text-white">
                                {club.nom_club}
                              </div>

                              <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                                {club.departement_club
                                  ? `Département ${club.departement_club}`
                                  : "Département non renseigné"}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:text-amber-300">
                              <FaBuilding />
                              Aucun club
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                          {reglement.formule || "—"}
                        </td>

                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {paymentMethod}
                        </td>

                        <td className="whitespace-nowrap px-4 py-3 text-right font-bold text-slate-900 dark:text-white">
                          {cf.format(Number(reglement.prix_total || 0))}
                        </td>

                        <td className="px-4 py-3">
                          <StatusBadge
                            tone={getReglementStatusTone(
                              reglement.statut_reglement,
                            )}
                          >
                            {reglement.statut_reglement || "—"}
                          </StatusBadge>
                        </td>

                        <td className="px-4 py-3">
                          <StatusBadge
                            tone={getPaymentStatusTone(
                              reglement.payment_status,
                            )}
                          >
                            {reglement.payment_status || "—"}
                          </StatusBadge>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <button
                              type="button"
                              onClick={() => openEditModal(reglement)}
                              title="Modifier l'achat"
                              className="rounded-xl border border-blue-200 bg-blue-50 p-2.5 text-blue-600 transition hover:bg-blue-100 dark:border-blue-900/50 dark:bg-blue-950/30 dark:text-blue-300"
                            >
                              <FaEdit />
                            </button>

                            <button
                              type="button"
                              onClick={() => setDeletingReglement(reglement)}
                              title="Supprimer l'achat"
                              className="rounded-xl border border-rose-200 bg-rose-50 p-2.5 text-rose-600 transition hover:bg-rose-100 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-300"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </DashboardCard>

        <div className="space-y-3 lg:hidden">
          {loading
            ? Array.from({ length: 4 }).map((_, index) => (
                <DashboardCard key={index} className="p-4">
                  <div className="h-32 animate-pulse rounded-xl bg-slate-200/70 dark:bg-slate-800" />
                </DashboardCard>
              ))
            : filteredReglements.map((reglement) => {
                const user = getReglementUser(reglement);

                const club = getClubForReglement(reglement, clubsByUser);

                const paymentMethod =
                  reglement.mode_reglement ||
                  (reglement.payment_status ? "Stripe" : "Autre");

                return (
                  <DashboardCard key={reglement.id} className="overflow-hidden">
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate font-bold text-slate-900 dark:text-white">
                            {getUserFullName(user)}
                          </div>

                          <div className="truncate text-xs text-slate-500 dark:text-slate-400">
                            {user.email || "—"}
                          </div>
                        </div>

                        <div className="shrink-0 text-lg font-bold text-slate-900 dark:text-white">
                          {cf.format(Number(reglement.prix_total || 0))}
                        </div>
                      </div>

                      <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/70 p-3 dark:border-blue-900/40 dark:bg-blue-950/20">
                        <div className="text-xs font-semibold uppercase tracking-wider text-blue-600 dark:text-blue-300">
                          Club
                        </div>

                        {club === undefined ? (
                          <div className="mt-2 h-5 w-48 animate-pulse rounded bg-blue-100 dark:bg-blue-900/40" />
                        ) : club ? (
                          <>
                            <div className="mt-1 font-bold text-slate-900 dark:text-white">
                              {club.nom_club}
                            </div>

                            <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                              {club.departement_club
                                ? `Département ${club.departement_club}`
                                : "Département non renseigné"}
                            </div>
                          </>
                        ) : (
                          <div className="mt-1 text-sm font-semibold text-amber-700 dark:text-amber-300">
                            Aucun club rattaché
                          </div>
                        )}
                      </div>

                      

                      <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <div className="text-xs font-semibold uppercase text-slate-400">
                            Formule
                          </div>

                          <div className="mt-1 font-medium text-slate-700 dark:text-slate-300">
                            {reglement.formule || "—"}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase text-slate-400">
                            Règlement
                          </div>

                          <div className="mt-1 font-medium text-slate-700 dark:text-slate-300">
                            {paymentMethod}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase text-slate-400">
                            Date
                          </div>

                          <div className="mt-1 text-slate-700 dark:text-slate-300">
                            {formatDate(reglement.date_achat)}
                          </div>
                        </div>

                        <div>
                          <div className="text-xs font-semibold uppercase text-slate-400">
                            Statut
                          </div>

                          <div className="mt-1">
                            <StatusBadge
                              tone={getReglementStatusTone(
                                reglement.statut_reglement,
                              )}
                            >
                              {reglement.statut_reglement || "—"}
                            </StatusBadge>
                          </div>
                        </div>
                      </div>

                      {reglement.note ? (
                        <div className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-950/50 dark:text-slate-300">
                          {reglement.note}
                        </div>
                      ) : null}
                    </div>

                    <div className="grid grid-cols-2 border-t border-slate-200 dark:border-slate-800">
                      <button
                        type="button"
                        onClick={() => openEditModal(reglement)}
                        className="inline-flex items-center justify-center gap-2 px-3 py-3 font-semibold text-blue-600 transition hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
                      >
                        <FaEdit />
                        Modifier
                      </button>

                      <button
                        type="button"
                        onClick={() => setDeletingReglement(reglement)}
                        className="inline-flex items-center justify-center gap-2 border-l border-slate-200 px-3 py-3 font-semibold text-rose-600 transition hover:bg-rose-50 dark:border-slate-800 dark:text-rose-300 dark:hover:bg-rose-950/30"
                      >
                        <FaTrash />
                        Supprimer
                      </button>
                    </div>
                  </DashboardCard>
                );
              })}

          {!loading && filteredReglements.length === 0 ? (
            <DashboardCard className="p-8 text-center">
              <FaCreditCard className="mx-auto mb-3 text-3xl text-slate-300 dark:text-slate-700" />

              <div className="font-semibold text-slate-700 dark:text-slate-300">
                Aucun achat ne correspond aux filtres.
              </div>
            </DashboardCard>
          ) : null}
        </div>
      </div>

      <ReglementFormModal
        open={formModalOpen}
        reglement={editingReglement}
        users={users}
        clubsByUser={clubsByUser}
        usersLoading={usersLoading}
        saving={saving}
        onLoadUserClub={onLoadUserClub}
        onClose={closeFormModal}
        onSubmit={handleSubmit}
      />

      <DeleteReglementModal
        reglement={deletingReglement}
        deleting={saving}
        onClose={() => {
          if (!saving) {
            setDeletingReglement(null);
          }
        }}
        onConfirm={handleDelete}
      />
    </>
  );
}
